import React, { useCallback, useMemo, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getHelpRouteOptions } from '../utils/helpConfig';
import './helpPage.css';

const categoryOptions = [
  'General',
  'Dashboard',
  'Bridges',
  'Simulation',
  'Repair Projects',
  'Masters Management',
  'Administration',
  'Settings',
  'Profile'
];

const emptyDocument = {
  title: '',
  slug: '',
  routePath: '',
  category: 'General',
  contentHtml: '',
  isPublished: true,
  isAdminOnly: false,
  sortOrder: 0
};

const Help = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyDocument);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const quillRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/help/documents');
      setDocuments(response.data.documents || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load help documents.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const matchesSearch = !searchTerm || doc.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !categoryFilter || doc.category === categoryFilter;
      const matchesRoute = !routeFilter || doc.routePath === routeFilter;
      return matchesSearch && matchesCategory && matchesRoute;
    });
  }, [documents, searchTerm, categoryFilter, routeFilter]);

  const groupedDocuments = useMemo(() => {
    return filteredDocuments.reduce((acc, doc) => {
      const key = doc.category || 'General';
      if (!acc[key]) acc[key] = [];
      acc[key].push(doc);
      return acc;
    }, {});
  }, [filteredDocuments]);

  const toggleCategory = (cat) => {
    setCollapsedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const startCreate = () => {
    setSelectedDoc(null);
    setFormData({ ...emptyDocument });
    setIsEditing(true);
  };

  const startEdit = (doc) => {
    setSelectedDoc(doc);
    setFormData({
      title: doc.title,
      slug: doc.slug,
      routePath: doc.routePath || '',
      category: doc.category || 'General',
      contentHtml: doc.contentHtml || '',
      isPublished: doc.isPublished,
      isAdminOnly: doc.isAdminOnly,
      sortOrder: doc.sortOrder || 0
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    if (selectedDoc) {
      setFormData({
        title: selectedDoc.title,
        slug: selectedDoc.slug,
        routePath: selectedDoc.routePath || '',
        category: selectedDoc.category || 'General',
        contentHtml: selectedDoc.contentHtml || '',
        isPublished: selectedDoc.isPublished,
        isAdminOnly: selectedDoc.isAdminOnly,
        sortOrder: selectedDoc.sortOrder || 0
      });
    } else {
      setFormData({ ...emptyDocument });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedDoc?.id) {
        await api.put(`/help/document/${selectedDoc.id}`, formData);
      } else {
        await api.post('/help/documents', formData);
      }
      setIsEditing(false);
      await loadDocuments();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save help document.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await api.delete(`/help/document/${doc.id}`);
      if (selectedDoc?.id === doc.id) {
        setSelectedDoc(null);
        setIsEditing(false);
      }
      await loadDocuments();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete help document.');
    }
  };

  const handleQuillImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      try {
        const response = await api.post('/help/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const editor = quillRef.current;
        const range = editor?.getSelection(true);
        if (range && editor) {
          editor.insertEmbed(range.index, 'image', response.data.url, 'user');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Image upload failed.');
      }
    };
    input.click();
  }, []);

  const quillToolbar = useMemo(
    () => [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
    []
  );

  const routeOptions = useMemo(() => getHelpRouteOptions(), []);

  // Clean up Quill when leaving edit mode or when switching to a different document
  React.useEffect(() => {
    if (!isEditing && quillRef.current) {
      quillRef.current = null;
    }
  }, [isEditing]);

  const editingKey = isEditing ? `edit-${selectedDoc?.id ?? 'new'}` : 'none';

  React.useEffect(() => {
    if (!isEditing || !editorRef.current) return;

    if (!quillRef.current) {
      // Preload container with existing HTML so Quill shows it immediately
      const initialHtml = formData.contentHtml || '';
      if (initialHtml) {
        editorRef.current.innerHTML = initialHtml;
      }
      const editor = new Quill(editorRef.current, {
        theme: 'snow',
        modules: { toolbar: quillToolbar }
      });
      editor.on('text-change', () => {
        setFormData((prev) => ({
          ...prev,
          contentHtml: editor.root.innerHTML
        }));
      });
      editor.getModule('toolbar').addHandler('image', handleQuillImage);
      quillRef.current = editor;
    } else {
      // Already have an editor; sync content when formData.contentHtml changes (e.g. switched doc)
      const editor = quillRef.current;
      const nextHtml = formData.contentHtml || '';
      if (editor && editor.root.innerHTML !== nextHtml) {
        editor.root.innerHTML = nextHtml;
      }
    }
  }, [isEditing, formData.contentHtml, quillToolbar, handleQuillImage]);

  // When the edited document changes, clear Quill ref so the new mount gets a fresh editor
  React.useEffect(() => {
    return () => {
      quillRef.current = null;
    };
  }, [editingKey]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Help & Documentation</h1>
          <p className="text-sm text-slate-600">Find guidance for each page in the app.</p>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            onClick={startCreate}
          >
            Add Document
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 rounded bg-rose-50 text-rose-700 text-sm">{error}</div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          {!selectedDoc && !isEditing && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 grid gap-4 md:grid-cols-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Search</label>
                  <input
                    className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                    placeholder="Search documents"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Category</label>
                  <select
                    className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categoryOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Route</label>
                  <select
                    className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value)}
                  >
                    <option value="">All Routes</option>
                    {routeOptions.map((opt) => (
                      <option key={opt.path} value={opt.path}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {loading && <div className="text-sm text-slate-500">Loading documents...</div>}

              {!loading && Object.keys(groupedDocuments).length === 0 && (
                <div className="text-sm text-slate-500">No help documents yet.</div>
              )}

              {Object.entries(groupedDocuments).map(([category, docs]) => (
                <div key={category} className="bg-white border border-slate-200 rounded-lg shadow-sm">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                    onClick={() => toggleCategory(category)}
                  >
                    <span className="font-semibold text-slate-800">{category}</span>
                    <span className="text-sm text-slate-500">
                      {collapsedCategories[category] ? 'Show' : 'Hide'}
                    </span>
                  </button>
                  {!collapsedCategories[category] && (
                    <div className="border-t border-slate-200 divide-y divide-slate-200">
                      {docs.map((doc) => (
                        <div key={doc.id} className="px-4 py-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <button
                                type="button"
                                className="text-left text-primary-700 font-semibold hover:underline"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setIsEditing(false);
                                }}
                              >
                                {doc.title}
                              </button>
                              <p className="text-sm text-slate-600 mt-1">{doc.excerpt}</p>
                              <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                                {doc.routePath && (
                                  <span className="px-2 py-1 bg-slate-100 rounded">{doc.routePath}</span>
                                )}
                                {doc.isAdminOnly && (
                                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">Admin only</span>
                                )}
                                {!doc.isPublished && (
                                  <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded">Draft</span>
                                )}
                              </div>
                            </div>
                            {isAdmin && (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="text-xs text-slate-600 hover:text-slate-900"
                                  onClick={() => startEdit(doc)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-rose-600 hover:text-rose-800"
                                  onClick={() => handleDelete(doc)}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {(selectedDoc || isEditing) && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {selectedDoc?.title || 'New Document'}
                  </h2>
                  {selectedDoc?.routePath && (
                    <p className="text-xs text-slate-500">{selectedDoc.routePath}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="text-sm text-slate-600 hover:underline"
                  onClick={() => {
                    setSelectedDoc(null);
                    setIsEditing(false);
                  }}
                >
                  Back to list
                </button>
              </div>

              {!isEditing && selectedDoc && (
                <div
                  className="help-content"
                  dangerouslySetInnerHTML={{ __html: selectedDoc.contentHtml }}
                />
              )}

              {isEditing && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Title</label>
                      <input
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.title}
                        onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Slug</label>
                      <input
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.slug}
                        onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Route Path</label>
                      <select
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.routePath}
                        onChange={(e) => setFormData((prev) => ({ ...prev, routePath: e.target.value }))}
                      >
                        <option value="">General</option>
                        {routeOptions.map((opt) => (
                          <option key={opt.path} value={opt.path}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Category</label>
                      <select
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.category}
                        onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                      >
                        {categoryOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Sort Order</label>
                      <input
                        type="number"
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        value={formData.sortOrder}
                        onChange={(e) => setFormData((prev) => ({ ...prev, sortOrder: Number(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.isPublished}
                        onChange={(e) => setFormData((prev) => ({ ...prev, isPublished: e.target.checked }))}
                      />
                      Published
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.isAdminOnly}
                        onChange={(e) => setFormData((prev) => ({ ...prev, isAdminOnly: e.target.checked }))}
                      />
                      Admin only
                    </label>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-500">Content</label>
                    <div
                      key={editingKey}
                      ref={editorRef}
                      className="bg-white"
                      style={{ minHeight: '240px' }}
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 border border-slate-300 rounded-md text-slate-600"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {!isEditing && selectedDoc && isAdmin && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 border border-slate-300 rounded-md text-slate-700"
                    onClick={() => startEdit(selectedDoc)}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 h-fit">
          <h3 className="font-semibold text-slate-800">Help Tips</h3>
          <ul className="mt-3 text-sm text-slate-600 space-y-2">
            <li>Click any document to open it.</li>
            <li>Admins can create, edit, and publish documentation.</li>
            <li>Use the Help button in the header on any page to open the side panel.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Help;
